import { useEffect } from 'react';

/**
 * Custom hook that adds IDs to all H2-H6 headings in a container
 * Enables deep linking and improves accessibility
 *
 * @param enabled - Whether to run the effect (use for hydration guard)
 * @param containerRef - Ref to the container element (optional, defaults to document)
 */
export function useHeadingIds(enabled: boolean = true, containerRef?: React.RefObject<HTMLElement>) {
  useEffect(() => {
    // Skip if not enabled (e.g., during hydration)
    if (!enabled) return;

    const container = containerRef?.current || document;

    if (!container) return;

    // Find all H2-H6 headings
    const headings = container.querySelectorAll('h2, h3, h4, h5, h6');

    // Track used IDs to avoid duplicates
    const usedIds = new Set<string>();
    const idCounts: { [key: string]: number } = {};

    headings.forEach((heading) => {
      // Skip if heading already has an ID
      if (heading.id) {
        usedIds.add(heading.id);
        return;
      }

      // Get heading text content
      const text = heading.textContent || '';

      // Generate slug from text
      const baseId = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      // Skip if heading is empty or too short
      if (!baseId || baseId.length < 2) {
        return;
      }

      // Ensure unique ID
      let headingId = baseId;
      if (usedIds.has(headingId)) {
        idCounts[baseId] = (idCounts[baseId] || 1) + 1;
        headingId = `${baseId}-${idCounts[baseId]}`;
      }

      usedIds.add(headingId);

      // Add ID to heading
      heading.id = headingId;
    });
  }, [enabled, containerRef]);
}
